/**
 * DS-internal hook : ce qui paraît et s'en va en douceur, sans être démonté avant la fin de son départ.
 * `progress` va de 0 (parti) à 1 (là), à la durée `standard` et à la courbe de la marque, par le pilote JS (une
 * hauteur ou une largeur n'en ont pas d'autre) ; en mouvement réduit, ou quand `animate` est faux, aussitôt.
 *
 *  - `mounted` : à rendre — là, ou en train de partir. Un départ interrompu par un retour ne démonte rien.
 *  - `rested` : là, son arrivée finie — ce qui peut alors reprendre sa place dans la mise en page.
 *
 * Né du détail des barres du live (`<Reveal>`) et de la colonne de la visio au grand écran
 * (`pages/StudentLivePage`), qui faisaient chacun la même chose.
 */
import { useEffect, useRef, useState } from "react"
import { Animated, Easing } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "./useReduceMotion"

export function usePresence(
    shown: boolean,
    animate = true
): { progress: Animated.Value; mounted: boolean; rested: boolean } {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const progress = useRef(new Animated.Value(shown ? 1 : 0)).current
    // Le dernier mouvement : sa cible, et s'il est arrivé. Au montage, la valeur est déjà à sa place, rien ne
    // part ; un mouvement interrompu en chemin (un réglage qui change) repart d'où il en est.
    const last = useRef({ target: shown, arrived: true })
    // État d'interface : posé là, posé parti, ou en chemin (`null`).
    const [rest, setRest] = useState<"shown" | "gone" | null>(shown ? "shown" : "gone")

    useEffect(() => {
        const at = shown ? "shown" : "gone"
        const alreadyThere = last.current.target === shown && last.current.arrived
        if (reduceMotion || !animate || alreadyThere) {
            last.current = { target: shown, arrived: true }
            progress.setValue(shown ? 1 : 0)
            setRest(at)
            return undefined
        }
        last.current = { target: shown, arrived: false }
        setRest(null)
        const move = Animated.timing(progress, {
            toValue: shown ? 1 : 0,
            duration: theme.motion.standard,
            easing: Easing.bezier(...theme.motion.easing),
            useNativeDriver: false,
        })
        move.start(({ finished }) => {
            if (!finished) return
            last.current = { target: shown, arrived: true }
            setRest(at)
        })
        return () => move.stop()
    }, [shown, animate, reduceMotion, progress, theme.motion])

    return { progress, mounted: shown || rest !== "gone", rested: shown && rest === "shown" }
}
